import React, { useCallback, useLayoutEffect, useState } from 'react';
import { useIsFocused } from '@react-navigation/native';
import { theme } from '@garnish/constants';

import { useIsLoggedIn } from '@order/AuthMachine';
import type { SweetpassLandingPageCtaAction } from '@order/components';
import {
  LoadingAnimation,
  SweetpassLandingPage,
  SweetpassSignedOut,
  useSweetpassLandingPageContent,
} from '@order/components';
import {
  TierName,
  useFetchPlansByTierIdQuery,
  useOptInUserMutation,
} from '@order/graphql';
import { useSweetpassTier } from '@order/hooks';
import { useLocalizationContext } from '@order/Localization';
import { useTelemetry, useTrackEventEffect } from '@order/Telemetry';

import { SweetpassConfirmationDialog } from '../../components/SweetpassConfirmationDialog';
import type { SweetpassUpgradeScreenProps } from '../../navigation/AppNavigation.props';

// ────────────────────────────────────────────────────────────────────────────────

export const SweetpassUpgradeScreen = ({
  navigation,
  route,
}: SweetpassUpgradeScreenProps) => {
  const { t } = useLocalizationContext();
  const { track } = useTelemetry();
  const isLoggedIn = useIsLoggedIn();
  const isScreenFocused = useIsFocused();

  const { redirectToAfterUpgrade, redirectToOrderId } = route.params ?? {};

  // ─── State ───────────────────────────────────────────────────────────

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  // ─── Queries ─────────────────────────────────────────────────────────

  const {
    isSweetpassEnabled,
    isSweetpassTierLoading,
    sweetpassNextTierId,
    sweetpassNextTierName,
    refetchSweetpassTier,
  } = useSweetpassTier('cache-and-network');

  const hasAcceptableTier =
    sweetpassNextTierName === 'SWEETPASS' ||
    sweetpassNextTierName === 'SWEETPASS_PLUS';
  const tag = hasAcceptableTier
    ? SWEETPASS_UPGRADE_TAGS[sweetpassNextTierName]
    : undefined;

  const nextTierId = sweetpassNextTierId ?? '';

  const [plansByTierId] = useFetchPlansByTierIdQuery({
    variables: { tierId: nextTierId },
    pause: !nextTierId,
  });

  const [optInMutationResponse, optInMutation] = useOptInUserMutation();
  const isLoadingOptInFreeTier =
    optInMutationResponse.fetching || optInMutationResponse.stale;

  const isLoadingPlanId = plansByTierId?.fetching;
  const planId =
    plansByTierId.data?.fetchPlansByTierId?.[0]?.braintreePlanId ?? '';

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleCtaConfirmation = useCallback(
    async (action?: string) => {
      if (action === 'join_sweetpass') {
        track('sweetpass_account.opt_in.tapped');
        const { data } = await optInMutation();
        const hasOptedInToFreeTier = Boolean(data?.optInFreeTier);

        if (hasOptedInToFreeTier) {
          refetchSweetpassTier({ requestPolicy: 'cache-and-network' });
          navigation.navigate('MainTabs', {
            screen: 'LoyaltyTab',
            params: { screen: 'SweetpassHome' },
          });
        }

        return;
      }

      track('sweetpass_account.upgrade_cta.tapped');
      navigation.navigate('Modal', {
        screen: 'SweetpassCheckout',
        params: { planId, redirectToAfterUpgrade, redirectToOrderId },
      });
    },
    [
      navigation,
      planId,
      redirectToAfterUpgrade,
      redirectToOrderId,
      track,
      refetchSweetpassTier,
      optInMutation,
    ],
  );

  const handleCtaPress = useCallback(
    (action?: SweetpassLandingPageCtaAction) => {
      if (!action) return;

      if (action === 'join_sweetpass') {
        setShowConfirmationModal(true);

        return;
      }

      void handleCtaConfirmation(action);
    },
    [handleCtaConfirmation],
  );

  const handleDismissModal = useCallback(() => {
    setShowConfirmationModal(false);
  }, []);

  /**
   * Redirects logged-out customers to the login, then to Sweetpass Upgrade screen.
   */
  const handlePressSignIn = useCallback(() => {
    navigation.replace('Auth', {
      screen: 'Login',
      params: { redirect: 'sweetpassUpgrade' },
    });
  }, [navigation]);

  // ─────────────────────────────────────────────────────────────────────

  const sweetpassLandingPageContent = useSweetpassLandingPageContent({
    tag,
    pause: !isSweetpassEnabled,
    onCtaPress: handleCtaPress,
  });

  const {
    content,
    fetching,
    hasContent: hasLandingPageContent,
  } = sweetpassLandingPageContent;

  // ─── Flags ───────────────────────────────────────────────────────────

  const isLoading = isLoadingPlanId || isSweetpassTierLoading || fetching;

  const hasContent =
    isSweetpassEnabled &&
    hasLandingPageContent !== undefined &&
    nextTierId !== undefined;
  const shouldRedirectToHome =
    isLoggedIn && isScreenFocused && !isLoading && !hasContent;

  // ─────────────────────────────────────────────────────────────────────

  const headerTitle =
    sweetpassNextTierName === TierName.SweetpassPlus
      ? t('sweetpass-plus')
      : t('sweetpass');

  // ─── Effects ─────────────────────────────────────────────────────────

  useTrackEventEffect({
    name: 'sweetpass_account.review_modal.viewed',
    skip: isLoading || shouldRedirectToHome,
  });

  // Reset header background color
  useLayoutEffect(() => {
    navigation.setOptions({
      headerStyle: {
        backgroundColor: theme.colors.OATMEAL,
      },
    });
  }, [navigation, shouldRedirectToHome]);

  useLayoutEffect(() => {
    if (shouldRedirectToHome) {
      navigation.navigate('MainTabs', {
        screen: 'HomeTab',
        params: { screen: 'Home' },
      });
    }
  }, [navigation, shouldRedirectToHome]);

  // ─────────────────────────────────────────────────────────────────────

  const onSweetpassDialogConfirm = useCallback(() => {
    void handleCtaConfirmation(content?.cta?.action);
  }, [content?.cta?.action, handleCtaConfirmation]);

  // ─────────────────────────────────────────────────────────────────────

  if (!isLoggedIn) {
    return (
      <SweetpassSignedOut
        testID="sweetpass-signed-out"
        onPressSignIn={handlePressSignIn}
      />
    );
  }

  if (isLoading) return <LoadingAnimation />;

  if (!hasContent) return null;

  return (
    <>
      <SweetpassLandingPage
        title={headerTitle}
        headerContent={content.header}
        benefitsContent={content.benefits}
        ctaContent={content.cta}
      />

      <SweetpassConfirmationDialog
        visible={showConfirmationModal}
        isLoading={isLoadingOptInFreeTier}
        onDismiss={handleDismissModal}
        onConfirm={onSweetpassDialogConfirm}
      />
    </>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const SWEETPASS_UPGRADE_TAGS: Record<string, SweetpassUpgradePageTag> = {
  SWEETPASS: 'join-sweetpass',
  SWEETPASS_PLUS: 'upgrade-to-sweetpass-plus',
};

// ─── Types ───────────────────────────────────────────────────────────────────

type SweetpassUpgradePageTag = 'join-sweetpass' | 'upgrade-to-sweetpass-plus';
